/**
 * TIMPRISER — firmans standardprislista, milpriset och momssatsen.
 *
 * SKÄRMEN FINNS FÖR ATT FIRMAN ANNARS INTE KAN RAPPORTERA EN TIMME.
 * CREATE_COMPANY lägger med flit upp en TOM standardprislista, och beslut 15
 * säger att priset FRYSES på tidraden när den sparas. Saknas rollens pris finns
 * inget att frysa: servern svarar PRICE_MISSING och raden sparas inte alls —
 * hellre det än en rad utan pris som börjar ändra sig när prislistan justeras.
 * En ny firma står alltså still tills den här skärmen är ifylld, även när
 * momenten finns.
 *
 * MILPRISET LIGGER PÅ SAMMA SKÄRM, OCH DET ÄR INTE EN SLASKLÅDA. Firmans
 * milpris seedas till 0 (schema/company.sql) och fryses på VARJE milrad precis
 * som timpriset fryses på tidraden. Står det kvar på 0 faktureras varje
 * rapporterad mil som noll kronor utan att någon märker något — inget fel, inget
 * meddelande, bara en nolla i underlaget. Därför sägs det rakt ut överst på
 * skärmen, i klartext, så länge det står så.
 *
 * FYRA ROLLER, INTE PROTOTYPENS SEX. Kontraktet har PriceRole =
 * Exclude<Role, 'bookkeeper'> — chef, montör, lärling, inhyrd — och listan nedan
 * är REPORTING_ROLES, kontraktets egen. Appboken talar om förman och
 * maskinförare; de finns inte i schemat och uppfinns inte här (se LUCKOR).
 * Bokföraren rapporterar aldrig tid och kan inte ha ett timpris.
 *
 * BARA CHEFEN SKRIVER. 'put:price_rate', 'delete:price_rate' och
 * 'put:company_settings' är MANAGER_ONLY i SYNC_RULES, och bokföraren står inte
 * i EN ENDA rad där (beslut 07) trots att han har full vy och SER priserna.
 * Han får därför samma skärm utan fält och utan knapp.
 *
 * OP_ID MYNTAS EN GÅNG OCH ÄNDRAS ALDRIG (beslut 09). När Spara trycks fryses en
 * PLAN — en operation per ändrad rad, med sina op_id — och fälten låses. En
 * omsändning skickar SAMMA operationer igen och får 'duplicate' i stället för en
 * andra skrivning; de som redan gått igenom skickas inte om alls.
 *
 * TVÅ AV TRE TAL FRYSES. TIMPRISET fryses på tidraden och MILPRISET på milraden,
 * båda när raden sparas (beslut 15) — de rör därför aldrig historiken. MOMSEN
 * GÖR DET INTE: kontraktet har ingen fryst momskolumn, och
 * worker/src/modules/views/basis-view.ts läser company_settings.vat_rate_hundredths
 * LIVE varje gång ett fakturaunderlag byggs, också för perioder som redan är
 * attesterade, låsta och fakturamärkta. En ändring från 25 till 12 byter alltså
 * momssats på varje gammalt underlag och varje CSV-export i samma sekund.
 * Skärmen säger det rakt ut vid momsfältet och frågar en gång innan den sparar
 * — den lovar inte längre att "ingenting här rör historiken".
 */

import { useState } from 'react';
import { REPORTING_ROLES } from '@delat/routes';
import type {
  CompanySettingsRow,
  OpId,
  PriceListRow,
  PriceRole,
  SyncOp,
} from '@delat/types';
import {
  FAKTOR,
  Felruta,
  Installningsrad,
  Knapp,
  Laddar,
  Listrad,
  Snabbrad,
  Talfalt,
  Tomtext,
  Yta,
  tillHeltal,
} from '../ui';
import { granska } from '../api';
import type { Klartidfel } from '../api';
import { visaOren } from '../app/datum';
import { useInloggad } from '../app/session';
import { glomAllaFirmaminnen } from '../app/firmaminnen';
import { ROLLNAMN } from './oversikt';
import {
  MAX_MOMS_HUNDRADELAR,
  MAX_ORE_PER_MIL,
  MAX_ORE_PER_TIMME,
  andratPrisOp,
  arKlar,
  borttagetPrisOp,
  forstaMisslyckade,
  installningarOp,
  nyttPrisOp,
  prisForRoll,
  skickaOps,
  useTimprislager,
} from './timpriser-data';
import type { Prislager } from './timpriser-data';

/* == Tal till text och tillbaka ==========================================
   ENHETEN ÄR HELTAL I MINSTA ENHET (ui/Falt.tsx): 550 kr är 55000 ören och
   25 % är 2500 hundradelar. Vägen IN går genom <Talfalt> och tillHeltal();
   vägen UT skrivs här, och den måste vara EXAKT. franHeltal() rundar till ett
   bestämt antal decimaler, och ett pris på 552,50 kr som visas som "553" och
   sparas om hade tyst ändrat kundens faktura med femtio öre.
   ======================================================================= */

/** 55000 -> "550". 55250 -> "552,50". Ingen avrundning, någonsin. */
const kronortext = (oren: number): string =>
  oren % 100 === 0 ? String(oren / 100) : (oren / 100).toFixed(2).replace('.', ',');

/** 2500 -> "25". 612 -> "6,12". Momsen ligger i hundradels procent. */
const procenttext = (hundradelar: number): string =>
  hundradelar % 100 === 0
    ? String(hundradelar / 100)
    : (hundradelar / 100).toFixed(2).replace('.', ',');

/** 'manager' -> 'Chef'. Orden är ROLLNAMN:s, versalen är radens. */
const rollrubrik = (roll: PriceRole): string =>
  ROLLNAMN[roll].charAt(0).toUpperCase() + ROLLNAMN[roll].slice(1);

const ROLLHJALP: Readonly<Record<PriceRole, string | undefined>> = {
  manager: 'Chefen rapporterar tid som alla andra. Det här är priset på hans timmar.',
  worker: undefined,
  apprentice: undefined,
  // Beslut 17: gästen är anställd i ett annat företag och rapporterar gratis
  // hos er. Priset här är vad NI tar av ER kund för hans timme — inte vad
  // inhyrningsfirman fakturerar er.
  guest: 'Vad du tar av din kund för en inhyrd timme. Inte vad du själv betalar för den.',
};

/** Den röda raden överst så länge milpriset står på 0. */
const Milvarning = () => (
  <Yta>
    <p style={{ margin: 0, color: 'var(--accent)' }}>
      <b>Firmans milpris står på 0 kr.</b> Varje mil någon rapporterar faktureras
      då som noll kronor — den syns i underlaget men är värd ingenting. Sätt
      priset under Firmans priser.
    </p>
  </Yta>
);

/**
 * MOMSEN FRYSES INTE, och det står ordagrant likadant i chefens vy, i
 * bokförarens och i bekräftelserutan. Samma mening tre gånger är med flit: den
 * som läst den vid fältet ska känna igen den när han trycker.
 */
const MOMSEN_FRYSES_INTE =
  'Momsen fryses inte. Ändrar du den här byter varje fakturaunderlag momssats, också perioder som redan är fakturerade.';

/* == Skärmen ============================================================= */

export const Timpriser = () => {
  const { firmaId, arChef, arLast } = useInloggad();
  const { lage, laddaOm } = useTimprislager(firmaId);
  const [sparat, sattSparat] = useState(false);

  if (lage.status === 'laddar') return <Laddar rader={6} />;
  if (lage.status === 'fel') {
    return (
      <Felruta
        fel={lage.fel}
        atgard={
          <Knapp variant="piller" onClick={laddaOm}>
            Försök igen
          </Knapp>
        }
      />
    );
  }

  const lager = lage.lager;
  const { prislista, installningar } = lager;

  // BÅDA RADERNA SKA FINNAS: schemat seedar company_settings och seedCompany
  // lägger upp standardlistan. Saknas de har svaret inte den fulla vyn, och då
  // är det inget skärmen kan laga genom att skapa nya rader — den säger det.
  if (prislista === null || installningar === null) {
    return (
      <>
        <Yta rubrik="Priserna kom inte med">
          <p style={{ margin: 0 }}>
            Firmans standardprislista och inställningar följer med hämtningen bara
            för chefen och bokföraren. Kom de inte med hjälper det inte att lägga
            upp nya — hämta om, och står det kvar är det något som ska felsökas.
          </p>
        </Yta>
        <Knapp onClick={laddaOm}>Hämta om</Knapp>
      </>
    );
  }

  return (
    <>
      {installningar.mileage_ore_per_10km === 0 && <Milvarning />}

      {lager.avkortat && (
        <Tomtext>
          Firman har fler rader än vad som hämtades i ett svep. Priserna nedan kan
          vara ofullständiga — hämta om innan du ändrar något.
        </Tomtext>
      )}

      {arChef ? (
        <Prisformular
          // NYCKELN ÄR MED FLIT. Formuläret fylls från raderna EN gång, när det
          // monteras. Efter en lyckad sparning hämtas firman om, versionerna
          // stiger, nyckeln byter värde — och formuläret monteras om med de nya
          // värdena i stället för att en useEffect ska hålla två sanningar i takt.
          key={nyckelFor(lager, installningar)}
          lager={lager}
          prislista={prislista}
          installningar={installningar}
          arLast={arLast}
          onHamtaOm={(harSparats) => {
            sattSparat(harSparats);
            // ALLA LAGREN, inte bara prislagret. Ett nyss ifyllt timpris är det
            // som gör skillnad på PRICE_MISSING och en sparad timme, och
            // tidrapporten håller sin egen kopia i en annan modul.
            glomAllaFirmaminnen();
            laddaOm();
          }}
          onAndrad={() => sattSparat(false)}
        />
      ) : (
        <Prisvisning lager={lager} installningar={installningar} />
      )}

      {sparat && arChef && <Tomtext>Priserna är sparade.</Tomtext>}
    </>
  );
};

/** Versionerna i handen. Byter någon av dem har formuläret gammalt innehåll. */
const nyckelFor = (lager: Prislager, installningar: CompanySettingsRow): string =>
  [
    `i${String(installningar.version)}`,
    ...lager.prisrader.map((rad) => `${rad.role}:${String(rad.version)}`),
  ].join('|');

/* == Bokförarens vy ======================================================
   Han SER hela laget och alla priser (FULL_VIEW_ROLES) men förekommer inte i en
   enda rad i SYNC_RULES — beslut 07. Alltså samma uppgifter, utan fält.
   ======================================================================= */

const Prisvisning = ({
  lager,
  installningar,
}: {
  readonly lager: Prislager;
  readonly installningar: CompanySettingsRow;
}) => (
  <>
    <Yta
      rubrik="Timpriser"
      rakning={`${lager.prisrader.length} av ${REPORTING_ROLES.length}`}
    >
      {REPORTING_ROLES.map((roll) => {
        const rad = prisForRoll(lager, roll);
        return (
          <Listrad
            key={roll}
            titel={rollrubrik(roll)}
            under="per timme, ex moms"
            varde={rad === null ? 'inget pris' : visaOren(rad.ore_per_hour)}
          />
        );
      })}
    </Yta>

    {lager.prisrader.length < REPORTING_ROLES.length && (
      <Tomtext>
        En roll utan pris kan inte rapportera tid alls — servern stoppar raden
        i stället för att spara den prislös.
      </Tomtext>
    )}

    <Yta rubrik="Firmans priser">
      <Installningsrad
        etikett="Milpris"
        varde={`${visaOren(installningar.mileage_ore_per_10km)} per mil, ex moms`}
      />
      <Installningsrad
        etikett="Moms"
        varde={`${procenttext(installningar.vat_rate_hundredths)} %`}
      />
    </Yta>

    <Tomtext>
      Du ser priserna men ändrar dem inte — det gör chefen. Timpriset fryses på
      tidraden och milpriset på milraden när raden sparas, så ett ändrat pris rör
      aldrig en rad som redan är rapporterad.
    </Tomtext>
    <Tomtext>{MOMSEN_FRYSES_INTE}</Tomtext>
  </>
);

/* == Chefens formulär ==================================================== */

interface Formularprops {
  readonly lager: Prislager;
  readonly prislista: PriceListRow;
  readonly installningar: CompanySettingsRow;
  readonly arLast: boolean;
  /**
   * Hämta firman på nytt och montera om formuläret. `harSparats` säger bara om
   * beskedet "Priserna är sparade" ska stå kvar — hämtningen görs likadant.
   *
   * DEN ANROPAS ÄVEN NÄR BARA EN DEL GICK IGENOM, och det är hela poängen: en
   * tillämpad operation har höjt radens version, och en ny plan byggd på det
   * lager vi står med hade burit en base_version som inte finns längre. Det ger
   * SYNC_CONFLICT — ett fel användaren inte gjort och inte kan laga utan att
   * ändå hämta om.
   */
  readonly onHamtaOm: (harSparats: boolean) => void;
  readonly onAndrad: () => void;
}

interface Plan {
  /** Operationerna i den ordning de ska köras. Frusna vid första trycket. */
  readonly ops: readonly SyncOp[];
}

/** Frågan som ställs EN gång innan en momsändring går iväg. Se filhuvudet. */
interface Momsfraga {
  readonly plan: Plan;
  readonly fran: string;
  readonly till: string;
}

const Prisformular = ({
  lager,
  prislista,
  installningar,
  arLast,
  onHamtaOm,
  onAndrad,
}: Formularprops) => {
  const { firmaId } = useInloggad();

  const [priser, sattPriser] = useState<Readonly<Record<string, string>>>(() => {
    const start: Record<string, string> = {};
    for (const roll of REPORTING_ROLES) {
      const rad = prisForRoll(lager, roll);
      start[roll] = rad === null ? '' : kronortext(rad.ore_per_hour);
    }
    return start;
  });
  const [milpris, sattMilpris] = useState(() =>
    kronortext(installningar.mileage_ore_per_10km),
  );
  const [moms, sattMoms] = useState(() => procenttext(installningar.vat_rate_hundredths));

  const [plan, sattPlan] = useState<Plan | null>(null);
  const [momsfraga, sattMomsfraga] = useState<Momsfraga | null>(null);
  const [klara, sattKlara] = useState<readonly OpId[]>([]);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<Readonly<Record<string, string>>>({});

  const last = plan !== null;

  /**
   * ETT RÄTTAT FÄLT SLUTAR SE FEL UT DIREKT. Felen sätts av byggPlan() och
   * togs tidigare bort först vid NÄSTA tryck på Spara — så den som rättade
   * "Priset kan inte vara minus" till 550 fick den röda texten kvar under
   * fältet. På en skärm vars hela poäng är att chefen ska våga lita på talen
   * han ser är det inte en småsak. 'inget' ("Ingenting är ändrat.") ryker med:
   * nu ÄR något ändrat.
   */
  const rensaFalt = (nyckel: string): void => {
    sattMomsfraga(null);
    sattFaltfel((gamla) => {
      if (gamla[nyckel] === undefined && gamla['inget'] === undefined) return gamla;
      const nya = { ...gamla };
      delete nya[nyckel];
      delete nya['inget'];
      return nya;
    });
  };

  const skrivPris = (roll: PriceRole, varde: string): void => {
    sattPriser((gamla) => ({ ...gamla, [roll]: varde }));
    rensaFalt(roll);
    onAndrad();
  };

  /**
   * Klientens egen förkontroll. Servern gör den riktiga mot schemats CHECK
   * (ore_per_hour 0-1000000, mileage_ore_per_10km 0-1000000,
   * vat_rate_hundredths 0-10000) — den här finns för att kunna säga det INNAN
   * chefen tryckt, inte för att ersätta serverns.
   */
  const byggPlan = (): Plan | null => {
    const brister: Record<string, string> = {};
    const ops: SyncOp[] = [];

    for (const roll of REPORTING_ROLES) {
      const text = (priser[roll] ?? '').trim();
      const rad = prisForRoll(lager, roll);

      if (text === '') {
        // TOMT FÄLT = ingen prisrad. Finns raden ska den bort; fanns den inte
        // är det ingenting att göra.
        if (rad !== null) ops.push(borttagetPrisOp(rad));
        continue;
      }

      const oren = tillHeltal(text, FAKTOR.kronor_oren);
      if (oren === null) {
        brister[roll] = 'Skriv priset i kronor, till exempel 550.';
        continue;
      }
      if (oren < 0) {
        brister[roll] = 'Priset kan inte vara minus.';
        continue;
      }
      if (oren > MAX_ORE_PER_TIMME) {
        brister[roll] = `Högst ${visaOren(MAX_ORE_PER_TIMME)} per timme.`;
        continue;
      }
      if (rad === null) {
        ops.push(nyttPrisOp(prislista.id, roll, granska.orePerTimme(oren, 'ore_per_hour')).op);
      } else if (oren !== Number(rad.ore_per_hour)) {
        ops.push(andratPrisOp(rad, granska.orePerTimme(oren, 'ore_per_hour')));
      }
    }

    // FIRMANS PRISER. Båda kolumnerna är NOT NULL, så ett tomt fält är inte
    // "orört" utan ett värde som saknas — 0 skrivs som en nolla.
    const milOren = tillHeltal(milpris.trim(), FAKTOR.kronor_oren);
    if (milpris.trim() === '') brister['milpris'] = 'Skriv 0 om ni inte fakturerar mil.';
    else if (milOren === null) brister['milpris'] = 'Skriv priset i kronor, till exempel 45.';
    else if (milOren < 0) brister['milpris'] = 'Milpriset kan inte vara minus.';
    else if (milOren > MAX_ORE_PER_MIL) {
      brister['milpris'] = `Högst ${visaOren(MAX_ORE_PER_MIL)} per mil.`;
    }

    const momsHundradelar = tillHeltal(moms.trim(), FAKTOR.procent_hundradel);
    if (moms.trim() === '') brister['moms'] = 'Skriv momssatsen, till exempel 25.';
    else if (momsHundradelar === null) brister['moms'] = 'Skriv momsen i procent, till exempel 25.';
    else if (momsHundradelar < 0 || momsHundradelar > MAX_MOMS_HUNDRADELAR) {
      brister['moms'] = 'Momsen ligger mellan 0 och 100 procent.';
    }

    if (Object.keys(brister).length > 0 || milOren === null || momsHundradelar === null) {
      sattFaltfel(brister);
      return null;
    }
    sattFaltfel({});

    if (
      milOren !== Number(installningar.mileage_ore_per_10km) ||
      momsHundradelar !== Number(installningar.vat_rate_hundredths)
    ) {
      ops.push(
        installningarOp(
          installningar,
          granska.orePerMil(milOren, 'mileage_ore_per_10km'),
          granska.hundradelar(momsHundradelar, 'vat_rate_hundredths'),
        ),
      );
    }

    if (ops.length === 0) {
      sattFaltfel({ inget: 'Ingenting är ändrat.' });
      return null;
    }
    return { ops };
  };

  /**
   * MOMSSATSEN SOM ÄR PÅ VÄG ATT BYTAS, eller null. Läses ur fälten och inte ur
   * planen: `installningarOp` bär både milpriset och momsen i samma operation,
   * så planen kan inte skilja på "chefen ändrade milpriset" och "chefen ändrade
   * momsen".
   */
  const momsbytet = (): { readonly fran: string; readonly till: string } | null => {
    const hundradelar = tillHeltal(moms.trim(), FAKTOR.procent_hundradel);
    if (hundradelar === null) return null;
    const nuvarande = Number(installningar.vat_rate_hundredths);
    if (hundradelar === nuvarande) return null;
    return { fran: procenttext(nuvarande), till: procenttext(hundradelar) };
  };

  const spara = (): void => {
    const aktuell = plan ?? byggPlan();
    if (aktuell === null) return;

    // FRÅGAN STÄLLS EN GÅNG, och bara på en FÖRSTA sparning: ett omtag på en
    // redan frusen plan har chefen redan svarat ja till.
    if (plan === null) {
      const byte = momsbytet();
      if (byte !== null) {
        sattMomsfraga({ plan: aktuell, ...byte });
        return;
      }
    }
    genomfor(aktuell);
  };

  const genomfor = (aktuell: Plan): void => {
    sattMomsfraga(null);
    if (plan === null) sattPlan(aktuell);

    const attSkicka = aktuell.ops.filter((op) => !klara.includes(op.op_id));
    if (attSkicka.length === 0) {
      onHamtaOm(true);
      return;
    }

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, attSkicka);
      // Alltid av igen, oavsett utfall: en knapp som fastnar i "Sparar ..."
      // efter ett tappat nät är en app man startar om.
      sattSparar(false);
      if (!svar.ok) {
        sattFel(svar.fel);
        return;
      }
      const nyaKlara = [...klara];
      for (const op of attSkicka) if (arKlar(svar.varde, op.op_id)) nyaKlara.push(op.op_id);
      sattKlara(nyaKlara);
      const opsfel = forstaMisslyckade(svar.varde);
      if (opsfel !== null) {
        sattFel(opsfel);
        return;
      }
      onHamtaOm(true);
    })();
  };

  /**
   * SYNC_CONFLICT och ROW_DUPLICATE betyder båda att raderna på servern inte ser
   * ut som vi trodde — någon annan har ändrat priset, eller en tidigare sparning
   * kom fram utan att vi såg svaret. Ingen omsändning i världen lagar det;
   * enda vägen är att hämta priserna på nytt.
   */
  const kraverOmhamtning =
    fel !== null &&
    fel.slag === 'api' &&
    (fel.code === 'SYNC_CONFLICT' || fel.code === 'ROW_DUPLICATE');

  return (
    <>
      <Yta rubrik="Timpriser" rakning="kr per timme, ex moms">
        {REPORTING_ROLES.map((roll) => (
          <Talfalt
            key={roll}
            etikett={rollrubrik(roll)}
            varde={priser[roll] ?? ''}
            onAndra={(varde) => skrivPris(roll, varde)}
            faktor={FAKTOR.kronor_oren}
            suffix="kr"
            disabled={last || sparar}
            placeholder="550"
            hjalp={
              (priser[roll] ?? '').trim() === ''
                ? 'Tomt = inget pris. Ingen med den rollen kan rapportera tid.'
                : ROLLHJALP[roll]
            }
            {...(faltfel[roll] === undefined ? {} : { fel: faltfel[roll] })}
          />
        ))}
      </Yta>

      <Yta rubrik="Firmans priser">
        {/* NOLLAN ÄR INTE ETT FEL. Hjälptexten ovanför ber uttryckligen om den
            ("Skriv 0 om ni inte fakturerar mil"), och ett fullt giltigt och
            sparbart värde ska inte få aria-invalid och röd ram — chefen hade
            ingen väg att bli av med felet utan att fakturera mil han inte kör.
            Följden står som VARNING i stället, och `fel` är kvar för det som
            faktiskt stoppar en sparning: tomt, minus, över taket. */}
        <Talfalt
          etikett="Milpris"
          varde={milpris}
          onAndra={(varde) => {
            sattMilpris(varde);
            rensaFalt('milpris');
            onAndrad();
          }}
          faktor={FAKTOR.kronor_oren}
          suffix="kr"
          disabled={last || sparar}
          placeholder="45"
          hjalp="Kronor per mil, ex moms. Det du tar av kunden — inte bilersättningen till montören."
          {...(faltfel['milpris'] === undefined ? {} : { fel: faltfel['milpris'] })}
          {...(tillHeltal(milpris.trim(), FAKTOR.kronor_oren) === 0
            ? { varning: 'Står på 0 — varje rapporterad mil blir noll kronor på fakturan.' }
            : {})}
        />
        <Talfalt
          etikett="Moms"
          varde={moms}
          onAndra={(varde) => {
            sattMoms(varde);
            rensaFalt('moms');
            onAndrad();
          }}
          faktor={FAKTOR.procent_hundradel}
          suffix="%"
          disabled={last || sparar}
          placeholder="25"
          hjalp="Följer med fakturaunderlaget ut. Klartid lägger aldrig på momsen själv — varje belopp i appen är ex moms."
          {...(faltfel['moms'] === undefined ? {} : { fel: faltfel['moms'] })}
          varning={MOMSEN_FRYSES_INTE}
        />
      </Yta>

      {faltfel['inget'] !== undefined && <Tomtext>{faltfel['inget']}</Tomtext>}

      {fel !== null && (
        <Felruta
          fel={fel}
          {...(kraverOmhamtning
            ? {
                atgard: (
                  <Knapp variant="piller" onClick={() => onHamtaOm(klara.length > 0)}>
                    Hämta priserna igen
                  </Knapp>
                ),
              }
            : {})}
        />
      )}

      {/* MOMSFRÅGAN. Ställs en gång, med det gamla och det nya talet utskrivna,
          innan en momsändring går iväg — den enda ändringen på skärmen som slår
          igenom bakåt på redan fakturerade perioder. */}
      {momsfraga !== null && (
        <Yta rubrik={`Byta moms från ${momsfraga.fran} % till ${momsfraga.till} %?`}>
          <p style={{ margin: 0 }}>
            {MOMSEN_FRYSES_INTE} Timpriset och milpriset rörs inte — de är frysta
            på sina rader.
          </p>
          <div style={{ marginTop: '0.7rem' }}>
            <Snabbrad>
              <Knapp variant="snabb" onClick={() => sattMomsfraga(null)}>
                Låt momsen vara
              </Knapp>
              <Knapp
                variant="snabb"
                disabled={arLast || sparar}
                onClick={() => genomfor(momsfraga.plan)}
              >
                Ja, byt till {momsfraga.till} %
              </Knapp>
            </Snabbrad>
          </div>
        </Yta>
      )}

      {momsfraga === null && (
        <Knapp
          disabled={arLast || sparar}
          onClick={spara}
          title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
        >
          {sparar ? 'Sparar ...' : plan === null ? 'Spara priserna' : 'Försök igen'}
        </Knapp>
      )}

      {last && !sparar && (
        <div style={{ marginTop: '0.5rem' }}>
          <Knapp
            variant="stor-tyst"
            onClick={() => {
              // GICK EN DEL IGENOM står raderna på nya versioner, och den planen
              // kan inte byggas om ur det lager vi har. Då hämtas firman om i
              // stället; fälten fylls från det som faktiskt står på servern.
              if (klara.length > 0) {
                onHamtaOm(true);
                return;
              }
              sattPlan(null);
              sattFel(null);
            }}
          >
            Ändra uppgifterna
          </Knapp>
        </div>
      )}

      {last && klara.length > 0 && (
        <Tomtext>
          En del av sparningen gick igenom. Tryck Försök igen — de rader som redan
          är sparade skickas inte om. Ändrar du uppgifterna i stället hämtas
          priserna om, så att du ser vad som faktiskt är sparat.
        </Tomtext>
      )}

      {arLast && (
        <Tomtext>
          Prenumerationen är pausad. Priserna går att läsa och exportera som
          vanligt, men inget nytt kan sparas förrän betalningen är ordnad.
        </Tomtext>
      )}

      <Tomtext>
        Priset sitter på ROLLEN och inte på personen — den dag en ny montör börjar
        finns priset redan. TIMPRISET fryses på tidraden och MILPRISET på
        milraden när raden sparas, så ett ändrat pris här rör aldrig en timme
        eller en mil som redan är rapporterad.
      </Tomtext>
      <Tomtext>{MOMSEN_FRYSES_INTE}</Tomtext>
    </>
  );
};
